import { useGetCurrentUserQuery } from "@/shared/services";
import { ONBOARDING_EVENTS, track } from "@features/analytics";
import { useIsOnboardingCohort } from "@features/onboarding/cohort";
// Deep imports: the `@features/platform-onboarding` barrel eagerly re-exports
// components, so a value import through it would pull floating-ui into the
// BaseLayout chunk that every route loads.
import { usePlatformTourStoreShallow } from "@features/platform-onboarding/store";
import { useAppSelector } from "@store";
import { selectIsAnyDialogOpen } from "@store/dialogs/selectors";
import { IconPlayerPlayFilled, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { findPageIntro } from "./config";
import { dismissPageIntro, isPageIntroDismissed } from "./store";

/** Intros already counted as viewed in this page load. */
const viewedKeys = new Set<string>();

export const PageIntroPopup = () => {
  const location = useLocation();
  const { data: me } = useGetCurrentUserQuery();
  const isAnyDialogOpen = useAppSelector(selectIsAnyDialogOpen);
  const phase = usePlatformTourStoreShallow((s) => s.phase);
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const isCohort = useIsOnboardingCohort(me?.createdAt);

  const intro = findPageIntro(location.pathname);
  const isVisible =
    !!intro &&
    isCohort &&
    dismissedKey !== intro.key &&
    !isPageIntroDismissed(me?.oktaId, intro.key) &&
    !isAnyDialogOpen &&
    phase !== "tour";
  const visibleKey = isVisible ? intro.key : null;

  useEffect(() => {
    if (!visibleKey || viewedKeys.has(visibleKey)) return;
    viewedKeys.add(visibleKey);
    track(ONBOARDING_EVENTS.VIEW_PAGE_INTRO, { page: visibleKey });
  }, [visibleKey]);

  if (!intro || !isVisible) return null;

  const handleDismiss = () => {
    track(ONBOARDING_EVENTS.DISMISS_PAGE_INTRO, { page: intro.key });
    setDismissedKey(intro.key);
    dismissPageIntro(me?.oktaId, intro.key);
  };

  return (
    <div className="tw-fixed tw-bottom-6 tw-right-6 tw-z-[9999] tw-w-[362px] tw-rounded-2xl tw-bg-white tw-p-5 tw-shadow-md tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5">
      <button
        type="button"
        aria-label={`Dismiss ${intro.title}`}
        onClick={handleDismiss}
        className="tw-absolute tw-right-2 tw-top-2 tw-z-10 tw-flex tw-size-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-text-blue tw-shadow-sm tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5 hover:tw-bg-primary-50 focus-visible:tw-outline-2 focus-visible:tw-outline-blue"
      >
        <IconX className="tw-size-4" />
      </button>
      {intro.videoUrl ? (
        <video
          key={intro.key}
          src={intro.videoUrl}
          autoPlay
          muted
          loop
          playsInline
          controls
          preload="metadata"
          className="tw-block tw-aspect-video tw-w-full tw-rounded-xl tw-bg-primary-50 tw-object-cover tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5"
        />
      ) : (
        <div className="tw-flex tw-h-[180px] tw-w-full tw-items-center tw-justify-center tw-rounded-xl tw-bg-primary-50 tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5">
          <div className="tw-flex tw-size-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-300/50">
            <IconPlayerPlayFilled size={20} className="tw-text-white" />
          </div>
        </div>
      )}
      <h3 className="tw-mb-0 tw-mt-4 tw-font-sans tw-text-xl tw-font-bold tw-text-blue">
        {intro.title}
      </h3>
      <p className="tw-mb-0 tw-mt-2 tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-blue-500">
        {intro.description}
      </p>
    </div>
  );
};
